import React, { useState, useEffect } from 'react';
import Icon from '../AppIcon';
import Button from './Button';
import { wishlistsService } from '../../services/wishlists';

const WishlistModal = ({ isOpen, onClose, product }) => {
  const [wishlists, setWishlists] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newWishlistName, setNewWishlistName] = useState('');
  const [error, setError] = useState('');

  // Fetch user's wishlists when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchWishlists();
    }
  }, [isOpen]);

  const fetchWishlists = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await wishlistsService.getWishlists();
      setWishlists(Array.isArray(data) ? data : data.results || []);
    } catch (error) {
      console.error('Error fetching wishlists:', error);
      setError('Failed to load wishlists');
    } finally {
      setLoading(false);
    }
  };

  const addToWishlist = async (wishlistId) => {
    try {
      setError('');
      await wishlistsService.addProductToWishlist(wishlistId, product.id);
      
      // Success feedback
      const wishlist = wishlists.find(w => w.id === wishlistId);
      showSuccessMessage(`Added to "${wishlist.name}"`);
      onClose();
    } catch (error) {
      console.error('Error adding to wishlist:', error);
      if (error.response?.status === 400 && error.response?.data?.error?.includes('already in wishlist')) {
        setError('This product is already in that wishlist');
      } else {
        setError('Failed to add to wishlist');
      }
    }
  };

  const createAndAddToWishlist = async () => {
    if (!newWishlistName.trim()) {
      setError('Please enter a wishlist name');
      return;
    }

    try {
      setCreating(true);
      setError('');

      // Create new wishlist
      const newWishlist = await wishlistsService.createWishlist({
        name: newWishlistName.trim()
      });

      // Add product to the new wishlist
      await wishlistsService.addProductToWishlist(newWishlist.id, product.id);

      showSuccessMessage(`Created "${newWishlistName}" and added product!`);
      onClose();
      setNewWishlistName('');

    } catch (error) {
      console.error('Error creating wishlist:', error);
      if (error.response?.data?.name) {
        setError(error.response.data.name[0] || 'Failed to create wishlist');
      } else {
        setError('Failed to create wishlist');
      }
    } finally {
      setCreating(false);
    }
  };

  const showSuccessMessage = (message) => {
    // Create a simple toast notification
    const toast = document.createElement('div');
    toast.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 transition-all duration-300';
    toast.textContent = message;
    
    document.body.appendChild(toast);
    
    // Animate in
    setTimeout(() => {
      toast.style.transform = 'translateX(0)';
      toast.style.opacity = '1';
    }, 100);
    
    // Remove after 3 seconds
    setTimeout(() => {
      toast.style.transform = 'translateX(100%)';
      toast.style.opacity = '0';
      setTimeout(() => {
        if (document.body.contains(toast)) {
          document.body.removeChild(toast);
        }
      }, 300);
    }, 3000);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div 
        className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[80vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <h2 className="text-xl font-semibold text-gray-900">Add to Wishlist</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <Icon name="X" size={20} className="text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {/* Loading State */}
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : (
            <>
              {/* Existing Wishlists */}
              {wishlists.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-sm font-medium text-gray-700 mb-3">Your Wishlists</h3>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {wishlists.map((wishlist) => (
                      <button
                        key={wishlist.id}
                        onClick={() => addToWishlist(wishlist.id)}
                        className="w-full flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-left"
                      >
                        <div>
                          <p className="font-medium text-gray-900">{wishlist.name}</p>
                          <p className="text-sm text-gray-500">
                            {wishlist.product_count} {wishlist.product_count === 1 ? 'item' : 'items'}
                          </p>
                        </div>
                        <Icon name="Plus" size={16} className="text-gray-400" />
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Create New Wishlist */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-3">Create New Wishlist</h3>
                <div className="flex space-x-3">
                  <input
                    type="text"
                    value={newWishlistName}
                    onChange={(e) => setNewWishlistName(e.target.value)}
                    placeholder="New Wishlist Name"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        createAndAddToWishlist();
                      }
                    }}
                  />
                  <Button
                    variant="primary"
                    onClick={createAndAddToWishlist}
                    disabled={creating || !newWishlistName.trim()}
                    className="px-4 py-2"
                  >
                    {creating ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    ) : (
                      'Create & Add'
                    )}
                  </Button>
                </div>
              </div>

              {/* No Wishlists State */}
              {wishlists.length === 0 && !loading && (
                <div className="text-center py-4 mb-4">
                  <Icon name="Heart" size={32} className="text-gray-300 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">You don't have any wishlists yet.</p>
                  <p className="text-sm text-gray-500">Create your first one below!</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default WishlistModal;